import { AccountFeeInfoSchema } from '@lyra/core/api/types/private.get_account'
import BodyText from '@lyra/core/components/BodyText'
import Button from '@lyra/core/components/Button'
import ButtonGroup from '@lyra/core/components/ButtonGroup'
import DataLabelRow from '@lyra/core/components/DataLabelRow'
import LabelText from '@lyra/core/components/LabelText'
import Section from '@lyra/core/components/Section'
import TooltipText from '@lyra/core/components/TooltipText'
import formatPercentage from '@lyra/core/utils/formatPercentage'
import formatUSD from '@lyra/core/utils/formatUSD'
import CopyButton from '@lyra/web/components/common/CopyButton'
import PayoffChart, { PayoffLegData } from '@lyra/web/components/common/PayoffChart'
import { isMainnet } from '@lyra/web/constants/env'
import { InstrumentType, Ticker } from '@lyra/web/constants/instruments'
import { DEFAULT_FEES, OrderQuote, OrderQuoteParams, OrderType } from '@lyra/web/constants/order'
import Orderbook from '@lyra/web/containers/common/Orderbook'
import TradeFeed from '@lyra/web/containers/common/TradeFeed'
import useAuth from '@lyra/web/hooks/useAuth'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import { getBestOfferPrice, parseInstrumentName } from '@lyra/web/utils/instruments'
import { getLeverageFromSize, getOrderQuotePremiums } from '@lyra/web/utils/order'
import { useMemo, useState } from 'react'
import { XStack, YStack } from 'tamagui'

import TradeFormRewardsRow from '../TradeFormRewardsRow'

type Props = {
  ticker: Ticker
  params: OrderQuoteParams
  quote?: OrderQuote | null
  isLoading?: boolean
  onPressLimitPrice: (limitPrice: number) => void
}

type Tab = 'summary' | 'book' | 'trades'

const getFeeRate = (
  instrumentType: InstrumentType,
  feeInfo?: AccountFeeInfoSchema
): { maker: number; taker: number } => {
  switch (instrumentType) {
    case InstrumentType.Options:
      return {
        maker: parseFloat(
          feeInfo?.option_maker_fee ?? DEFAULT_FEES[instrumentType].maker.toString()
        ),
        taker: parseFloat(
          feeInfo?.option_taker_fee ?? DEFAULT_FEES[instrumentType].taker.toString()
        ),
      }
    case InstrumentType.Perps:
      return {
        maker: parseFloat(feeInfo?.perp_maker_fee ?? DEFAULT_FEES[instrumentType].maker.toString()),
        taker: parseFloat(feeInfo?.perp_taker_fee ?? DEFAULT_FEES[instrumentType].taker.toString()),
      }
    case InstrumentType.Spot:
      return {
        maker: parseFloat(feeInfo?.spot_maker_fee ?? DEFAULT_FEES[instrumentType].maker.toString()),
        taker: parseFloat(feeInfo?.spot_taker_fee ?? DEFAULT_FEES[instrumentType].taker.toString()),
      }
  }
}

const TradeFormReceipt = ({ ticker, params, quote, isLoading, onPressLimitPrice }: Props) => {
  const liquidationPrice = quote?.post_liquidation_price ? +quote.post_liquidation_price : 0
  const pnl = quote ? +quote.estimated_realized_pnl : 0

  const { account } = useAuth()

  const isTriggerOrder = !!params.trigger_type

  const fee = quote ? +quote.estimated_fee : 0
  const baseFee = quote ? +ticker.base_fee : 0
  const amount = +params.amount
  const isBuy = params.direction === 'buy'

  const bbo = getBestOfferPrice(ticker, isBuy)
  const expectedFillPrice = quote ? +quote.estimated_fill_price : 0
  const estFillPrice = expectedFillPrice ? expectedFillPrice : bbo ? bbo : +ticker.mark_price

  const isMarketOrder = params.order_type === OrderType.Market && !params.trigger_type

  const spotPrice = +ticker.index_price

  const premiums = getOrderQuotePremiums(estFillPrice, +params.amount, isBuy, fee)
  const { instrument_name, direction } = params

  const payoffLegs: [PayoffLegData] = useMemo(() => {
    return [
      {
        instrument_name,
        direction,
        amount,
        premiums,
      },
    ]
  }, [amount, premiums, instrument_name, direction])

  const { subaccount } = useSubaccount()
  const openPosition = useMemo(
    () =>
      subaccount?.positions
        .filter((p) => !!+p.amount)
        .find((pos) => pos.instrument_name === ticker.instrument_name),
    [subaccount, ticker]
  )

  const instrument = parseInstrumentName(ticker.instrument_name)
  const isOption = instrument?.type === InstrumentType.Options
  const isPerp = instrument?.type === InstrumentType.Perps

  const showPayoffChart = !openPosition && isOption && !!premiums && !!amount && !isTriggerOrder

  const [_tab, setTab] = useState<Tab>('summary')
  const tab: Tab = isOption ? _tab : 'summary'

  const fillPrice = params.order_type === OrderType.Market ? spotPrice : +params.limit_price
  const leverage = useMemo(() => {
    return subaccount
      ? getLeverageFromSize(subaccount, openPosition, isBuy ? +amount : -amount, fillPrice)
      : 0
  }, [subaccount, openPosition, isBuy, amount, fillPrice])

  const showLeverage = isPerp && !isTriggerOrder && subaccount

  if (!instrument) {
    console.warn('Invalid instrument')
    // TODO: @earthtojake better warning here
    return null
  }

  const feeInfo = account?.fee_info
  const { maker: makerFeeRate, taker: takerFeeRate } = getFeeRate(instrument?.type, feeInfo)

  return (
    <>
      {/* Book and trades are visible for non-option order types */}
      {isOption ? (
        <Section.XStack>
          <ButtonGroup>
            <Button
              label="Summary"
              size="sm"
              isSelected={tab === 'summary'}
              onPress={() => setTab('summary')}
            />
            <Button
              label="Book"
              size="sm"
              isSelected={tab === 'book'}
              onPress={() => setTab('book')}
            />
            <Button
              label="Trades"
              size="sm"
              isSelected={tab === 'trades'}
              onPress={() => setTab('trades')}
            />
          </ButtonGroup>
        </Section.XStack>
      ) : null}
      {tab === 'summary' ? (
        <Section.YStack>
          {pnl ? (
            <DataLabelRow
              label="Profit / Loss"
              color={pnl > 0 ? 'green' : 'red'}
              value={formatUSD(pnl, { showSign: true })}
            />
          ) : showPayoffChart ? (
            <>
              <LabelText>Expected Profit / Loss</LabelText>
              <PayoffChart height={110} legs={payoffLegs} spotPrice={spotPrice} />
            </>
          ) : null}
          {isMainnet ? <TradeFormRewardsRow fee={fee} baseFee={baseFee} /> : null}
          <DataLabelRow
            label={
              <TooltipText
                tooltipContent={`Taker orders pay a ${formatPercentage(
                  takerFeeRate
                )} fee. Maker orders pay a ${formatPercentage(makerFeeRate)} fee.`}
                placement="top-end"
                label="Fees"
              />
            }
            value={isLoading ? '...' : formatUSD(fee)}
          />
          {showLeverage ? (
            <DataLabelRow
              label="Leverage"
              value={`${leverage > 0 ? 'Long ' : leverage < 0 ? 'Short ' : ''}${Math.abs(
                leverage
              ).toFixed(2)}x`}
            />
          ) : null}
          {isMarketOrder ? (
            <DataLabelRow
              label="Expected Price"
              value={isLoading ? '...' : expectedFillPrice ? formatUSD(expectedFillPrice) : 'None'}
            />
          ) : null}
          <DataLabelRow
            label="Liquidation Price"
            value={isLoading ? '...' : liquidationPrice ? formatUSD(liquidationPrice) : 'None'}
          />
          <XStack justifyContent="space-between">
            <TooltipText
              label="Developers"
              stayOpenOnHover
              tooltipContent={
                <YStack gap="$1">
                  <XStack gap="$2" alignItems="center">
                    <BodyText color="secondary">Copy Parameters</BodyText>
                    <CopyButton marginLeft="auto" value={JSON.stringify(params)} size="sm" />
                  </XStack>
                  <XStack gap="$2" justifyContent="space-between" alignItems="center">
                    <BodyText color="secondary">Order Type</BodyText>
                    <BodyText>{params.order_type}</BodyText>
                  </XStack>
                  <XStack gap="$2" justifyContent="space-between" alignItems="center">
                    <BodyText color="secondary">Amount</BodyText>
                    <BodyText>{+params.amount}</BodyText>
                  </XStack>
                  <XStack gap="$2" justifyContent="space-between" alignItems="center">
                    <BodyText color="secondary">Limit Price</BodyText>
                    <BodyText>{+params.limit_price}</BodyText>
                  </XStack>
                  <XStack gap="$2" justifyContent="space-between" alignItems="center">
                    <BodyText color="secondary">Trigger Type</BodyText>
                    <BodyText>{params.trigger_type ? params.trigger_type : 'None'}</BodyText>
                  </XStack>
                  <XStack gap="$2" justifyContent="space-between" alignItems="center">
                    <BodyText color="secondary">Trigger Price</BodyText>
                    <BodyText>{params.trigger_price ? +params.trigger_price : 'None'}</BodyText>
                  </XStack>
                </YStack>
              }
              placement="top-end"
            />
          </XStack>
        </Section.YStack>
      ) : tab === 'book' ? (
        <Section.Table>
          <Orderbook
            onPressTick={(price) => onPressLimitPrice(price)}
            numTicks={5}
            instrumentName={ticker.instrument_name}
          />
        </Section.Table>
      ) : tab === 'trades' ? (
        <Section.Table>
          <TradeFeed instrumentName={ticker.instrument_name} pageSize={10} />
        </Section.Table>
      ) : null}
    </>
  )
}

export default TradeFormReceipt
